import { useState, useEffect, Key } from "react";
import { Link } from "react-router-dom";
import Icon from "@mui/material/Icon";
import Container from "@mui/material/Container";
import { Box, Avatar } from "@mui/material";
import SoftBox from "components/SoftBox";
import DefaultNavbarLink from "./DefaultNavbarLink";
import DefaultNavbarMobile from "./DefaultNavbarMobile";
import breakpoints from "../../../assets/theme/base/breakpoints";
import AllPages from "examples/ReuseFunctions/AllPages";
import { useThemeColor } from "context/ThemeContext";
import { buildClientLogoUrl } from "utils/clientLogoUrl";
import { contrastTextForHex } from "utils/colorContrast";
import scoutlogo from "assets/images/scoutlogo.png";

function DefaultNavbar({ transparent = false, light = false, action = false }) {
  const [mobileNavbarAnchor, setMobileNavbarAnchor] =
    useState<HTMLElement | null>(null);
  const [mobileView, setMobileView] = useState(false);
  const [navLogoBroken, setNavLogoBroken] = useState(false);

  const themeCtx = useThemeColor();
  const { pagesData: rawPagesData, logo } = AllPages();
  const pagesData = rawPagesData ?? [];

  const tenantLogo = buildClientLogoUrl(themeCtx?.publicLogoPath ?? null);
  const cmsLogo = logo ? buildClientLogoUrl(logo) : null;
  const primarySrc = tenantLogo ?? cmsLogo;

  useEffect(() => {
    setNavLogoBroken(false);
  }, [primarySrc]);

  const openMobileNavbar = (event: React.MouseEvent<HTMLElement>) => {
    setMobileNavbarAnchor(event.currentTarget);
  };
  const closeMobileNavbar = () => setMobileNavbarAnchor(null);

  useEffect(() => {
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.xl) {
        setMobileView(true);
        setMobileNavbarAnchor(null);
      } else {
        setMobileView(false);
        setMobileNavbarAnchor(null);
      }
    }
    window.addEventListener("resize", displayMobileNavbar);
    displayMobileNavbar();
    return () => window.removeEventListener("resize", displayMobileNavbar);
  }, []);

  const tc = themeCtx?.themeColor ?? "#2e7d32";
  const company = themeCtx?.publicCompanyName?.trim();
  const showTenantLetter = Boolean(company) && (!primarySrc || navLogoBroken);
const logoContent = (() => {
  if (primarySrc && !navLogoBroken) {
    return (
      <Box
        component="img"
        src={primarySrc}
        alt=""
        onError={() => setNavLogoBroken(true)}
        sx={{
          maxHeight: 44,
          maxWidth: 220,
          width: "auto",
          height: "auto",
          objectFit: "contain",
          display: "block",
        }}
      />
    );
  }
  if (showTenantLetter) {
    return (
      <Avatar
        sx={{
          width: 44,
          height: 44,
          bgcolor: tc,
          color: contrastTextForHex(tc),
          fontWeight: 700,
          fontSize: "1.1rem",
        }}
      >
        {company!.charAt(0).toUpperCase()}
      </Avatar>
    );
  }
  return (
    <Box
      component="img"
      src={scoutlogo}
      alt="Scout"
      sx={{
        maxHeight: 44,
        width: "auto",
        objectFit: "contain",
        display: "block",
      }}
    />
  );
})();

  return (
    <Box className="default-header">
      <Container>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <SoftBox
            className="logo"
            component={Link}
            lineHeight={1}
            sx={{ display: "flex", alignItems: "center", minHeight: 44 }}
          >
            {logoContent}
          </SoftBox>

          <SoftBox
            color="inherit"
            display={{ xs: "none", lg: "flex" }}
            m={0}
            p={0}
          >
            {pagesData?.map(
              (data: {
                page_route: Key | null | undefined;
                page_name: string;
              }) => (
                <DefaultNavbarLink
                  key={data.page_route}
                  icon="info"
                  name={data.page_name}
                  route={`/${data.page_route}`}
                  light={light}
                />
              )
            )}
            <DefaultNavbarLink
              icon="info"
              name="About us"
              route="/about"
              light={light}
            />
            <DefaultNavbarLink
              icon="contacts"
              name="Contact us"
              route="/contact"
              light={light}
            />
            <DefaultNavbarLink
              icon="account_circle"
              name="Employee sign in"
              route="/employee/sign-in"
              light={light}
            />
          </SoftBox>

          <SoftBox
            display={{ xs: "inline-block", lg: "none" }}
            lineHeight={0}
            py={1.5}
            pl={1.5}
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={openMobileNavbar}
          >
            <Icon
              fontSize="medium"
              sx={{ color: light ? "white" : "inherit" }}
            >
              {mobileNavbarAnchor ? "close" : "menu"}
            </Icon>
          </SoftBox>
        </SoftBox>

        {mobileView && (
          <DefaultNavbarMobile
            open={mobileNavbarAnchor}
            close={closeMobileNavbar}
            pagesData={pagesData}
          />
        )}
      </Container>
    </Box>
  );
}

export default DefaultNavbar;
